import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, X } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

const MessageNotification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    // Listen for new message events
    const handleNewMessage = (event) => {
      // Only show if NOT on messages page
      if (!location.pathname.includes('/messages')) {
        setNotification(event.detail);
      }
    };

    window.addEventListener('newMessageReceived', handleNewMessage);
    
    return () => {
      window.removeEventListener('newMessageReceived', handleNewMessage);
    };
  }, [location.pathname]);

  const handleViewMessage = () => {
    setNotification(null);
    navigate('/messages');
  };

  const handleDismiss = () => {
    setNotification(null);
  };

  if (!notification) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[100] animate-slide-up">
      <div className={`${
        isDark ? 'bg-[#2a2240] border-[#7B3FF2]' : 'bg-white border-gray-200'
      } border-2 rounded-2xl shadow-2xl max-w-sm overflow-hidden`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#7B3FF2] to-[#9059ff] p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-bold text-sm">New Message</span>
          </div>
          <button
            onClick={handleDismiss}
            className="text-white/80 hover:text-white p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="flex items-start gap-3 mb-3">
            <img 
              src={notification.senderPhoto}
              alt={notification.sender}
              className="w-12 h-12 rounded-full object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <h4 className={`text-sm font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {notification.sender}
              </h4>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                {notification.jobTitle}
              </p>
              <Badge className="bg-[#7B3FF2] text-white text-xs font-mono">
                {notification.jobId}
              </Badge>
            </div>
          </div>

          <div className={`${isDark ? 'bg-[#1e1534]' : 'bg-gray-50'} rounded-lg p-3 mb-3`}>
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'} line-clamp-2`}>
              {notification.message}
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleDismiss}
              variant="outline"
              size="sm"
              className={`flex-1 ${isDark ? 'border-gray-700' : 'border-gray-300'}`}
            >
              Dismiss
            </Button>
            <Button
              onClick={handleViewMessage}
              size="sm"
              className="flex-1 bg-gradient-to-r from-[#7DD3C0] to-[#5bb3a0] hover:from-[#6bc3b0] hover:to-[#4aa390] text-black font-semibold"
            >
              View Message
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageNotification;
